import React, { useEffect, useState } from 'react';
import { Link, Copy, Check } from 'lucide-react';
import { generateCandidateRegistrationLink, copyLinkToClipboard } from '@/features/rh/lib/candidateLink';
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton';

interface CandidatePublicLinkProps {
  jobRequestId: string | number;
  className?: string;
}

export const CandidatePublicLink: React.FC<CandidatePublicLinkProps> = ({
  jobRequestId,
  className = ''
}) => {
  const [copied, setCopied] = useState(false);
  const [publicLink, setPublicLink] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [linkError, setLinkError] = useState<string>('');

  const ensurePublicLink = async (): Promise<string | null> => {
    if (publicLink) return publicLink;

    try {
      setIsGenerating(true);
      setLinkError('');
      const link = await generateCandidateRegistrationLink(jobRequestId);
      setPublicLink(link);
      return link;
    } catch (errorCrudo: unknown) {
      const error = errorCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      // Fallback de compatibilidad: enlace con jobRequestId directo
      const fallbackLink = `${window.location.origin}/candidato/registro?jobRequestId=${encodeURIComponent(String(jobRequestId))}`;
      setPublicLink(fallbackLink);
      setLinkError(
        error?.message ||
          'No se pudo generar el token seguro; se generó un enlace alternativo.',
      );
      return fallbackLink;
    } finally {
      setIsGenerating(false);
    }
  };

  // El diseño muestra la dirección de entrada, sin el botón «Ver enlace» que
  // había antes, así que se genera al montar el bloque.
  useEffect(() => {
    if (!publicLink && !isGenerating) {
      void ensurePublicLink();
    }
  }, [publicLink, isGenerating]);


  const handleCopyLink = async () => {
    const link = await ensurePublicLink();
    if (!link) return;
    const success = await copyLinkToClipboard(link);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className={className}>
      <p className="mb-3 text-sm text-[#414141]">
        Enlace público para candidatos
      </p>

      <div className="flex items-center gap-3 rounded-xl border border-[#E0E0E0] px-4 py-3">
        <Link className="h-5 w-5 flex-shrink-0 text-[#3C5EB3]" />

        <span className="min-w-0 flex-1 truncate text-sm text-[#3C5EB3]" title={publicLink}>
          {publicLink || (isGenerating ? 'Generando enlace seguro…' : '')}
        </span>

        <button
          onClick={handleCopyLink}
          disabled={isGenerating}
          title="Copiar enlace"
          className={estiloDeBoton('secundario', 'flex-shrink-0')}
        >
          {copied ? (
            <>
              <Check className="h-4 w-4" />
              ¡Copiado!
            </>
          ) : (
            <>
              <Copy className="h-4 w-4" />
              Copiar
            </>
          )}
        </button>

      </div>

      {linkError && <p className="mt-2 text-xs text-red-600">{linkError}</p>}
    </div>
  );
};

export default CandidatePublicLink;
